/*
 * Settings → Integrations → Slack. Build step 1 of DESIGN-slack.md: connect, see who we found,
 * link the rest by hand, disconnect.
 *
 * ⚠️ **ONE CLICK FOR THE WHOLE COMPANY — THE OPPOSITE OF THE TEAMS CARD BELOW IT.** Teams needs
 * every person to add the app themselves (DESIGN-teams.md decision I). In Slack an admin adds it
 * once and HolidayHive can then message anyone in that workspace, so this card has a real
 * "Add to Slack" button where the Teams one has a download and four steps.
 *
 * ⚠️ **FINDING SOMEBODY SENDS THEM NOTHING.** Rob's decision B1 (find people by work email) was
 * agreed on exactly that understanding; whether anyone gets Slack messages is their own choice
 * on their profile (decision H). The card says so, so nobody presses Add to Slack expecting the
 * whole company to start getting messages.
 *
 * ⚠️ **"ADD TO SLACK" IS A POST, NOT A LINK.** The server ties the returning install to the
 * admin who asked; the browser is handed Slack's address and navigates itself
 * (routers/slack.py explains why a link would let a stranger's Slack be attached).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Hash, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { apiErrorMessage, slackApi } from '../lib/api';
import { formatDateGB } from '../lib/dates';
import { cardLine } from '../lib/slackDigest';
import IntegrationHeader from './IntegrationHeader';

/*
 * What Slack's redirect back to us means, in words. The server sends a status word and nothing
 * else (nothing sensitive may travel in that URL); this turns it into a sentence.
 */
export const OUTCOMES = {
  connected: { ok: true, text: 'Connected to Slack.' },
  refused: { ok: false, text: 'You cancelled on Slack’s screen, so nothing was connected.' },
  expired: { ok: false, text: 'That took too long or had already been used. Please press Add to Slack again.' },
  failed: { ok: false, text: 'Slack didn’t finish the connection. Please try again.' },
  taken: { ok: false, text: 'That Slack workspace is already connected to another HolidayHive company.' },
  org_install: { ok: false, text: 'That was an organisation-wide (Enterprise Grid) install, which HolidayHive doesn’t support yet. Add it to a single workspace instead.' },
  not_configured: { ok: false, text: 'Slack isn’t set up on this installation yet.' },
};

/* Why somebody wasn't found — one sentence each, naming the fix where there is one. */
export function reasonText(row, teamName) {
  const team = teamName ? `${teamName}’s Slack` : 'Slack';
  switch (row.reason) {
    case 'no_email': return 'No email address in HolidayHive';
    case 'not_in_slack': return `${row.detail || row.email} isn’t in ${team}`;
    case 'taken': return `That address’s Slack account (${row.detail}) is linked by hand to someone else`;
    case 'ambiguous': return `More than one match for ${row.detail}, so we didn’t guess`;
    case 'left_slack': return `Was linked to ${row.detail}, who’s no longer in Slack`;
    case 'not_checked': return 'Added since the last check — press Check again';
    case 'unlinked': return `Unlinked from ${row.detail || 'their Slack account'} — Check again looks for them by work email, or link by hand`;
    default: return 'Not found';
  }
}

/* How many of the people in Slack to show before "Show all" — the list is the long one in a
 * real company, and the people still to link come after it. */
const IN_SLACK_PREVIEW = 6;

export default function SlackIntegrationCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);           // 'connect' | 'rematch' | 'disconnect' | user id
  const [members, setMembers] = useState(null);     // loaded the first time someone links by hand
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(null);   // the user id being unlinked
  const [showAllInSlack, setShowAllInSlack] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [outcome, setOutcome] = useState(null);

  const load = useCallback(() => slackApi
    .status()
    .then((res) => setStatus(res.data))
    .catch(() => setStatus(null))
    .finally(() => setLoading(false)), []);

  useEffect(() => { load(); }, [load]);

  /*
   * ⚠️ Read once, then removed from the address bar — otherwise a refresh would show
   * "Connected to Slack." again, days later, beside a card that may say something else.
   */
  useEffect(() => {
    const word = searchParams.get('slack');
    if (!word) return;
    setOutcome(OUTCOMES[word] || OUTCOMES.failed);
    const next = new URLSearchParams(searchParams);
    next.delete('slack');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const connect = async () => {
    setBusy('connect');
    try {
      const res = await slackApi.connect();
      window.location.assign(res.data.authorize_url);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t start the connection to Slack.'));
      setBusy(null);
    }
  };

  const rematch = async () => {
    setBusy('rematch');
    try {
      const res = await slackApi.rematch();
      setStatus(res.data);
      setMembers(null);
      toast.success('Checked again.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t check Slack just now.'));
      load();
    } finally {
      setBusy(null);
    }
  };

  const loadMembers = async () => {
    if (members || loadingMembers) return;
    setLoadingMembers(true);
    try {
      const res = await slackApi.members();
      setMembers(res.data.members || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t fetch the people in your Slack.'));
    } finally {
      setLoadingMembers(false);
    }
  };

  /*
   * ⚠️ Fetched as soon as there is somebody to link — waiting for the first click on a
   * native <select> means it opens empty, and has to be opened again once the list arrives.
   */
  const needsMembers = !!status?.connected && (status?.not_found || []).length > 0;
  useEffect(() => {
    if (needsMembers && members === null) loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsMembers, members]);

  const linkByHand = async (userId, slackUserId) => {
    if (!slackUserId) return;
    setBusy(userId);
    try {
      const res = await slackApi.link(userId, slackUserId);
      setStatus(res.data);
      setMembers(null);
      toast.success('Linked.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t link that account.'));
    } finally {
      setBusy(null);
    }
  };

  /*
   * ⚠️ Decision H3b: only for people with no email address — they can't sign in, so this is
   * the only way their notifications can ever reach Slack. The server refuses it for anyone
   * who can sign in and choose for themselves.
   */
  const switchSlack = async (userId, on) => {
    setBusy(`switch-${userId}`);
    try {
      const res = await slackApi.setNotifications(userId, on);
      setStatus(res.data);
      toast.success(on ? 'Slack messages turned on.' : 'Slack messages turned off.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t change that.'));
    } finally {
      setBusy(null);
    }
  };

  /*
   * Rob, 15/10/2026: there was no way to undo a link made by hand. A link to the wrong
   * account sends that person's notifications into somebody else's Slack, so it must be
   * undoable without disconnecting the whole company.
   */
  const unlink = async (userId) => {
    setBusy(`unlink-${userId}`);
    try {
      const res = await slackApi.unlink(userId);
      setStatus(res.data);
      setMembers(null);
      setConfirmUnlink(null);
      toast.success('Unlinked.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t unlink that account.'));
    } finally {
      setBusy(null);
    }
  };

  /* What unlinking will mean for this person — said BEFORE the click, not after. */
  const unlinkConsequence = (row) => {
    if (!row.slack_on) return 'They don’t have Slack turned on, so nothing they get changes.';
    if (row.can_sign_in) return 'Their notifications stop going to that Slack account and come by email instead.';
    return `${row.name} has no email address, so nothing will reach them until they’re linked again.`;
  };

  const disconnect = async () => {
    setBusy('disconnect');
    try {
      const res = await slackApi.disconnect();
      setStatus(res.data);
      setConfirmDisconnect(false);
      setMembers(null);
      toast.success('Slack disconnected.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t disconnect Slack.'));
    } finally {
      setBusy(null);
    }
  };

  /*
   * The tidy top row (Rob, 27/10/2026 — easy-installs). ⚠️ "Add to Slack" sits on the right
   * only while there is something to add; once connected the row says so instead.
   */
  const headerWith = (action) => (
    <IntegrationHeader
      brand="slack"
      fallback={Hash}
      name="Slack"
      line="Approve leave from a Slack message, and get notifications there."
      place="Slack"
      status={status?.connected && (
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
              data-testid="slack-connected-pill">
          Connected
        </span>
      )}
      action={action}
    />
  );
  const header = headerWith(null);

  const outcomeBanner = outcome && (
    <div className={`flex items-start gap-2 p-3 rounded-md mb-4 border text-xs ${outcome.ok
      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
      : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="slack-outcome">
      {outcome.ok
        ? <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden="true" />
        : <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />}
      <span>{outcome.text}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="card-hive p-6" data-testid="integrations-slack-card">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (!status) {
    return (
      <div className="card-hive p-6" data-testid="integrations-slack-card">
        {header}
        <p className="text-xs text-muted-foreground">Couldn’t load the Slack connection. Please refresh the page.</p>
      </div>
    );
  }

  /* ── Not set up on this installation ─────────────────────────────────────────── */
  if (!status.configured && !status.connected) {
    return (
      <div className="card-hive p-6" data-testid="integrations-slack-card">
        {header}
        {outcomeBanner}
        <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200"
             data-testid="integrations-slack-unconfigured">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-amber-800">
            Slack isn’t set up on this installation yet. Add its three keys in
            Superadmin &rarr; API Keys, then come back here.
          </p>
        </div>
      </div>
    );
  }

  /* ── Not connected ────────────────────────────────────────────────────────────── */
  if (!status.connected) {
    const was = status.was_connected;
    return (
      <div className="card-hive p-6" data-testid="integrations-slack-card">
        {headerWith(
          <Button onClick={connect} disabled={busy === 'connect'} className="btn-primary" size="sm"
                  data-testid="slack-add">
            {busy === 'connect' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add to Slack
          </Button>,
        )}
        {outcomeBanner}
        {was && was.removed_at && (
          /*
           * ⚠️ "Removed" is a different story from "never connected". If Slack itself told us
           * the token stopped working, an admin must be able to see that — otherwise the only
           * symptom would be messages quietly stopping.
           */
          <p className="text-xs text-muted-foreground mb-4" data-testid="slack-was-connected">
            {was.last_error && ['token_revoked', 'account_inactive', 'invalid_auth'].includes(was.last_error)
              ? `HolidayHive was removed from ${was.team_name ? `the ${was.team_name} Slack workspace` : 'your Slack'} on Slack’s side on ${formatDateGB(was.removed_at)}.`
              : `Disconnected from ${was.team_name ? `the ${was.team_name} Slack workspace` : 'Slack'} on ${formatDateGB(was.removed_at)}.`}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          You’ll be taken to Slack to choose your workspace and allow HolidayHive in.
          Nobody’s notifications change — each person chooses Slack on their own profile.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          On Slack’s free plan a workspace can have 10 apps. If you’re at 10, Slack will ask you
          to remove one first.
        </p>
      </div>
    );
  }

  /* ── Connected ────────────────────────────────────────────────────────────────── */
  const notFound = status.not_found || [];
  const inSlack = status.in_slack || [];
  const freeMembers = (members || []).filter((m) => !m.linked_to);
  const shownInSlack = showAllInSlack ? inSlack : inSlack.slice(0, IN_SLACK_PREVIEW);

  return (
    <div className="card-hive p-6" data-testid="integrations-slack-card">
      {header}
      {outcomeBanner}

      <p className="text-sm text-muted-foreground -mt-2 mb-4" data-testid="slack-connected-line">
        {status.team_name ? `Workspace: ${status.team_name}` : 'Your Slack'}
        {status.installed_by ? ` · connected by ${status.installed_by}` : ''}
        {status.installed_at ? ` on ${formatDateGB(status.installed_at)}` : ''}
      </p>

      {/*
        * Slack build step 4. ⚠️ The who's-off channel is CHOSEN in Settings → Notifications
        * (Rob's A1), beside the digests it belongs to; this line only says where things stand
        * and links there, so nobody hunts for the picker on this card.
        */}
      <p className="text-xs text-muted-foreground -mt-2 mb-4" data-testid="slack-digest-line">
        {cardLine(status.digest).text}{' '}
        <Link to="/settings?tab=notifications" className="underline" data-testid="slack-digest-link">
          {cardLine(status.digest).link}
        </Link>
        {status.digest?.error && (
          <span className="text-amber-800"> The last post didn&rsquo;t go — the reason is shown there.</span>
        )}
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium" data-testid="slack-found-count">
          {/* ⚠️ Zero is an ordinary answer, never a failure — same trap as the Teams count. */}
          People · found {status.found} of {status.people}
        </p>
        <Button variant="outline" size="sm" onClick={rematch} disabled={busy === 'rematch'}
                data-testid="slack-rematch">
          {busy === 'rematch'
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : <RefreshCw className="w-4 h-4 mr-2" />}
          Check again
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Found by work email. Finding someone doesn’t message them — each person chooses Slack
        on their own profile.
        {status.last_matched_at ? ` Last checked ${formatDateGB(status.last_matched_at)}.` : ''}
      </p>

      {status.last_error && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 mt-3"
           data-testid="slack-last-error">
          Slack’s last answer was an error ({status.last_error})
          {status.last_error_at ? ` on ${formatDateGB(status.last_error_at)}` : ''}. Press Check again to retry.
        </p>
      )}

      {inSlack.length > 0 && (
        /*
         * ⚠️ THE LIST BEHIND "found X of Y" — Rob, 15/10/2026: "that two does not show
         * anywhere". Everybody we can message, however they were found: by work email, or
         * linked by hand (which is where Unlink and the no-email switch live). It comes FIRST
         * and is cut to a few rows when long: in a real company this list is the big one,
         * and the people still to link below it are what needs doing.
         */
        <div className="mt-5" data-testid="slack-in-slack">
          <h4 className="text-sm font-semibold mb-1">In Slack · {inSlack.length}</h4>
          <ul className="divide-y divide-border">
            {shownInSlack.map((row) => (
              <li key={row.user_id} className="py-2 flex flex-wrap items-center justify-between gap-2"
                  data-testid={`slack-in-slack-${row.user_id}`}>
                <div className="min-w-0">
                  <p className="text-sm">{row.name} &rarr; {row.slack_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {/*
                      * ⚠️ Short, because in a real company this line repeats for everybody.
                      * "Slack is on/off" is the person's own choice on their profile — except
                      * for somebody with no email address, where the switch beside it is it.
                      */}
                    {row.how === 'manual' ? 'Linked by hand' : 'Found by work email'}
                    {row.can_sign_in
                      ? ` · Slack is ${row.slack_on ? 'on' : 'off'}`
                      : ' · no email address, so Slack is the only way to reach them'}
                    {row.last_error ? ` · Slack’s last answer: ${row.last_error}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {row.how === 'manual' && !row.can_sign_in && (
                    /*
                     * ⚠️ Off until an admin turns it on: linking somebody doesn't change their
                     * notifications by itself (decision H). The words beside the switch say
                     * what it does, so it never reads as "linked = messaged".
                     */
                    <label className="flex items-center gap-2 text-xs text-muted-foreground whitespace-nowrap">
                      Slack messages
                      <Switch
                        checked={!!row.slack_on}
                        disabled={busy === `switch-${row.user_id}`}
                        onCheckedChange={(on) => switchSlack(row.user_id, on)}
                        aria-label={`Send ${row.name}’s notifications to Slack`}
                        data-testid={`slack-switch-${row.user_id}`}
                      />
                    </label>
                  )}
                  {row.how === 'manual' && confirmUnlink !== row.user_id && (
                    <button type="button" className="text-xs text-red-700 underline whitespace-nowrap"
                            onClick={() => setConfirmUnlink(row.user_id)}
                            data-testid={`slack-unlink-${row.user_id}`}>
                      Unlink
                    </button>
                  )}
                </div>
                {confirmUnlink === row.user_id && (
                  /*
                   * ⚠️ Inline, like Disconnect: it says what will happen to THIS person before
                   * anything changes, and it can't hide behind another window.
                   */
                  <div className="basis-full text-xs bg-muted/40 rounded-md p-2"
                       data-testid={`slack-unlink-confirm-${row.user_id}`}>
                    <p className="mb-2">
                      Unlink {row.name} from {row.slack_name}? {unlinkConsequence(row)}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" onClick={() => unlink(row.user_id)}
                              disabled={busy === `unlink-${row.user_id}`}
                              data-testid={`slack-unlink-yes-${row.user_id}`}>
                        {busy === `unlink-${row.user_id}` && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Unlink
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmUnlink(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {inSlack.length > IN_SLACK_PREVIEW && (
            <button type="button" className="text-xs underline mt-1"
                    onClick={() => setShowAllInSlack(!showAllInSlack)}
                    data-testid="slack-in-slack-toggle">
              {showAllInSlack ? 'Show fewer' : `Show all ${inSlack.length}`}
            </button>
          )}
        </div>
      )}


      {notFound.length > 0 && (
        <div className="mt-5" data-testid="slack-not-found">
          <h4 className="text-sm font-semibold mb-1">
            We couldn’t find {notFound.length} {notFound.length === 1 ? 'person' : 'people'} in Slack
          </h4>
          {/*
            * Rob, 15/10/2026: the Link by hand list looked broken with two names in it. It is
            * everybody in the company's Slack — so somebody who isn't there can't be in it.
            */}
          <p className="text-xs text-muted-foreground mb-1" data-testid="slack-link-hint">
            Link by hand lists everyone in {status.team_name ? `${status.team_name}’s` : 'your'} Slack
            who isn’t linked yet. Someone who isn’t in Slack needs inviting there first.
          </p>
          <ul className="divide-y divide-border">
            {notFound.map((row) => (
              <li key={row.user_id} className="py-2 flex flex-wrap items-center justify-between gap-2"
                  data-testid={`slack-not-found-${row.user_id}`}>
                <div className="min-w-0">
                  <p className="text-sm">{row.name}</p>
                  <p className="text-xs text-muted-foreground">{reasonText(row, status.team_name)}</p>
                </div>
                {/*
                  * ⚠️ The list arrives on first use, not with the card: it means asking Slack
                  * for everybody in the workspace, and most visits to this tab link nobody.
                  */}
                <select
                  className="h-8 max-w-[14rem] rounded-md border border-border bg-background px-2 text-xs"
                  defaultValue=""
                  onFocus={loadMembers}
                  onMouseDown={loadMembers}
                  onChange={(e) => linkByHand(row.user_id, e.target.value)}
                  disabled={busy === row.user_id}
                  aria-label={`Link ${row.name} to a Slack account by hand`}
                  data-testid={`slack-link-${row.user_id}`}
                >
                  <option value="" disabled>
                    {loadingMembers
                      ? 'Loading…'
                      : (members && freeMembers.length === 0
                        ? `Everyone in ${status.team_name ? `${status.team_name}’s` : 'your'} Slack is already linked`
                        : 'Link by hand…')}
                  </option>
                  {/*
                    * ⚠️ ONLY ACCOUNTS NOBODY HAS — Rob's answer A, 15/10/2026. Taken accounts
                    * used to be listed greyed out with "(linked to …)"; the In Slack list above
                    * now says who has each one, and in a company of 200 the greyed names would
                    * bury the few that can be picked. To reuse a taken account, Unlink it there.
                    */}
                  {freeMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}{m.email ? ` · ${m.email}` : ''}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-border">
        {!confirmDisconnect ? (
          <button type="button" className="text-xs text-red-700 underline"
                  onClick={() => setConfirmDisconnect(true)} data-testid="slack-disconnect">
            Disconnect Slack
          </button>
        ) : (
          /*
           * ⚠️ An inline confirmation, not window.confirm(): it says what will happen, and it
           * can't be lost behind another window.
           */
          <div className="text-xs" data-testid="slack-disconnect-confirm">
            <p className="mb-2">
              Disconnect Slack? HolidayHive will be removed from{' '}
              {status.team_name ? `the ${status.team_name} Slack workspace` : 'your Slack'} and
              nobody will get Slack messages from it. People you linked by hand are remembered if you
              reconnect.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" onClick={disconnect} disabled={busy === 'disconnect'}
                      data-testid="slack-disconnect-yes">
                {busy === 'disconnect' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Disconnect
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmDisconnect(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
